import React, { Component, Fragment } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { authenticateUser } from "../../actions/authenticateActions";
import { notify } from "../../util/notifications";
import Form from "react-validation/build/form";
import Button from "react-validation/build/button";
import { required } from "../../util/validationMethods";
import { getSubdomainInfo, getSubDomain } from "../../util/subdomainHelper";
import { getCookie, setCookie, deleteCookie } from "../../util/cookieHelper";
import TmInputGroup from "../../components/utilComponents/tmInputGroup/tmInputGroup";
import Background from "../../assets/img/bg_body.jpg";
import "./login.css";

/**
 * Login Component
 * ─────────────────────────────────────────────────────
 * CHANGES from previous version:
 *  1. handleSubmit wrapped in try/catch — network errors now surface to user
 *  2. Remember Me checkbox wired to cookies (username only, never password)
 *  3. Eliminated direct state mutation in onChangeValues
 *  4. Added isSubmitting guard to prevent double-submit
 *  5. Loading indicator while authenticating
 *  6. Inline error banner so user always sees what went wrong
 *  7. Redirect timer cleaned up on unmount (prevents memory leak)
 *  8. Removed stale dev comments and unused constants
 */

const REMEMBER_ME_COOKIE = "navinet_remember_user";
const REMEMBER_ME_DAYS = 30;

class Login extends Component {
  constructor(props) {
    super(props);

    // Pre-fill username from Remember Me cookie if present
    const savedUser = getCookie(REMEMBER_ME_COOKIE) || "";

    this.state = {
      userName: savedUser,
      password: "",
      rememberMe: !!savedUser,
      isSubmitting: false,
      loginError: null,
      subdomainError: false,
    };

    this._redirectTimer = null;
  }

  componentDidMount() {
    const subdomainInfo = getSubdomainInfo();

    if (subdomainInfo.isMainDomain) {
      this.setState({ subdomainError: true });
      this._redirectTimer = setTimeout(() => {
        this.props.history.push("/");
      }, 3000);
    }
  }

  componentWillUnmount() {
    if (this._redirectTimer) {
      clearTimeout(this._redirectTimer);
    }
  }

  /** Immutable state update — never mutate this.state directly */
  onChangeValues = (event) => {
    const { name, value } = event.currentTarget;
    this.setState({ [name]: value, loginError: null });
  };

  handleRememberMeChange = (event) => {
    this.setState({ rememberMe: event.target.checked });
  };

  handleSubmit = async () => {
    const { dispatch, history, location } = this.props;
    const { userName, password, rememberMe } = this.state;

    if (this.state.isSubmitting) return;

    this.setState({ isSubmitting: true, loginError: null });

    try {
      const subdomainInfo = getSubdomainInfo();
      const subdomain = subdomainInfo.subdomain || getSubDomain();

      const resp = await dispatch(
        authenticateUser(userName, password, subdomain)
      );

      if (!resp) {
        this.setState({
          isSubmitting: false,
          loginError: "Invalid username or password. Please try again.",
        });
        try {
          notify.createNotification(
            "error",
            "Invalid UserName/Password",
            this.props.refNotify
          );
        } catch (notifyErr) {
          console.warn("Notification display failed:", notifyErr);
        }
        return;
      }

      // Persist or clear Remember Me cookie (username only — never password)
      if (rememberMe) {
        setCookie(REMEMBER_ME_COOKIE, userName, REMEMBER_ME_DAYS);
      } else {
        deleteCookie(REMEMBER_ME_COOKIE);
      }

      // Password reset required on first login
      if (resp.loginUserResp.userDetails.isReset === 0) {
        const userid = resp.loginUserResp.userDetails.userId;
        const resetPwdToken = resp.pwdResetToken;
        history.push(
          `/resetPassword?forgetToken=${resetPwdToken}&userid=${userid}`
        );
      } else {
        const from = location.state?.from?.pathname || "/dispatch/dashboard";
        history.push(from);
      }
    } catch (error) {
      console.error("Login error:", error);

      const message =
        error?.response?.data?.statusMsg ||
        error?.message ||
        "Something went wrong. Please check your connection and try again.";

      this.setState({ isSubmitting: false, loginError: message });

      try {
        notify.createNotification("error", message, this.props.refNotify);
      } catch (notifyErr) {
        console.warn("Notification display failed:", notifyErr);
      }
    }
  };

  renderSubdomainError() {
    return (
      <section
        className="wrapper"
        style={{
          backgroundImage: `url(${Background})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "cover",
        }}
      >
        <div className="login-wrap d-flex align-items-center flex-wrap justify-content-center pd-20">
          <div className="login-box bg-white box-shadow pd-20 border-radius-5">
            <h6 className="text-center mb-20" style={{ color: "#dc3545" }}>
              <i
                className="fa fa-exclamation-triangle"
                style={{ fontSize: "48px", marginBottom: "16px" }}
              />
              <br />
              <strong>Invalid Access</strong>
            </h6>
            <p className="text-center">
              You cannot access the login page from the main domain.
              <br /><br />
              Please use your company code or domain to access your company portal.
              <br /><br />
              Redirecting to home page&hellip;
            </p>
          </div>
        </div>
      </section>
    );
  }

  render() {
    if (this.state.subdomainError) {
      return this.renderSubdomainError();
    }

    const { userName, password, rememberMe, isSubmitting, loginError } = this.state;

    return (
      <section
        className="wrapper"
        style={{
          backgroundImage: `url(${Background})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "cover",
        }}
      >
        <div className="login-wrap d-flex align-items-center flex-wrap justify-content-center pd-20">
          <div className="login-box bg-white box-shadow pd-20 border-radius-5">
            <img
              src={require("../../assets/images/NN Logo Final-04.jpg")}
              alt="NaviNet"
              className="login-img"
            />
            <h6 className="text-center mb-20">
              <strong>SIGN IN TO CONTINUE.</strong>
            </h6>

            {/* Inline error banner — always visible on failure */}
            {loginError && (
              <div className="login-error-banner">
                <i className="fa fa-exclamation-circle" style={{ marginRight: "6px" }} />
                {loginError}
              </div>
            )}

            <Form
              name="frmLogin"
              className="mb-lg"
              onSubmit={(e) => {
                e.preventDefault();
                this.handleSubmit();
              }}
            >
              <Fragment>
                <div className="input-group custom input-group-lg">
                  <TmInputGroup
                    id="userName"
                    name="userName"
                    type="text"
                    placeholder="Enter User name"
                    className="form-control"
                    value={userName}
                    validations={[required]}
                    onChange={this.onChangeValues}
                    requiredmsg="Please enter user name"
                    append="true"
                    appendText={
                      <div className="input-group-append custom">
                        <span className="input-group-text">
                          <i className="fa fa-user" aria-hidden="true" />
                        </span>
                      </div>
                    }
                  />
                </div>

                <div className="input-group custom input-group-lg">
                  <TmInputGroup
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Password"
                    className="form-control"
                    value={password}
                    validations={[required]}
                    onChange={this.onChangeValues}
                    requiredmsg="Please enter password"
                    append="true"
                    appendText={
                      <div className="input-group-append custom">
                        <span className="input-group-text">
                          <i className="fa fa-lock" aria-hidden="true" />
                        </span>
                      </div>
                    }
                  />
                </div>

                <div className="row mb-20">
                  <div className="col-sm-6">
                    <div className="custom-control custom-checkbox custom-control">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        name="rememberMe"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={this.handleRememberMeChange}
                      />
                      <label htmlFor="rememberMe" className="custom-control-label">
                        REMEMBER ME
                      </label>
                    </div>
                  </div>
                  <div className="col-sm-6 text-right">
                    <Link style={{ fontWeight: "490" }} to="/accountrecovery">
                      Account Recovery
                    </Link>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12">
                    <div className="input-group">
                      <Button
                        type="submit"
                        className="btn btn-outline-primary btn-lg btn-block"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Signing in\u2026" : "LOGIN"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Fragment>
            </Form>
          </div>
        </div>
      </section>
    );
  }
}

const mapStateToProps = (state) => ({
  authInfo: state.authInfo,
  refNotify: state.refNotify,
});

export default withRouter(connect(mapStateToProps)(Login));
